import React, { useRef, useEffect, useCallback } from 'react';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css'; // import styles

const WysiwygEditor = ({ value, onChange, placeholder = "Write your content here...", className = "", minHeight = "250px" }) => {
  const quillRef = useRef(null);
  
  // Handle pasted images - convert to base64 and insert at cursor position
  const handlePaste = useCallback((e) => {
    const clipboard = e.clipboardData;
    if (!clipboard || !clipboard.items) return;
    
    const items = clipboard.items;
    for (let i = 0; i < items.length; i++) {
      const item = items[i];
      if (item.type.indexOf('image') !== -1) {
        e.preventDefault();
        e.stopPropagation();
        
        const file = item.getAsFile();
        if (!file) continue;
        
        const reader = new FileReader();
        reader.onload = (event) => {
          const base64 = event.target.result;
          const quill = quillRef.current?.getEditor();
          if (quill) {
            const range = quill.getSelection(true);
            const index = range ? range.index : quill.getLength();
            quill.insertEmbed(index, 'image', base64);
            quill.setSelection(index + 1);
          }
        };
        reader.readAsDataURL(file);
        break; // Only handle the first image
      }
    }
  }, []);
  
  // Attach paste handler to the editor
  useEffect(() => {
    const quill = quillRef.current?.getEditor();
    if (quill) {
      const editorElement = quill.root;
      editorElement.addEventListener('paste', handlePaste);
      return () => {
        editorElement.removeEventListener('paste', handlePaste);
      };
    }
  }, [handlePaste]);

  // Quill modules to attach to editor
  const modules = {
    toolbar: [
      [{ 'header': [1, 2, 3, 4, 5, 6, false] }],
      ['bold', 'italic', 'underline', 'strike'],
      [{ 'list': 'ordered'}, { 'list': 'bullet' }],
      [{ 'color': [] }, { 'background': [] }],
      [{ 'align': [] }],
      ['link', 'image'],
      ['clean']
    ],
    clipboard: {
      matchVisual: false,
    }
  };

  // Quill editor formats
  const formats = [
    'header',
    'bold', 'italic', 'underline', 'strike',
    'list', 'bullet',
    'color', 'background',
    'align',
    'link', 'image'
  ];

  return (
    <div className={`wysiwyg-editor w-full ${className}`}>
      <ReactQuill
        ref={quillRef}
        theme="snow"
        value={value}
        onChange={onChange}
        modules={modules}
        formats={formats}
        placeholder={placeholder}
        className="w-full"
      />
      <style>{`
        .wysiwyg-editor {
          width: 100%;
        }
        .wysiwyg-editor .ql-editor {
          min-height: ${minHeight};
          font-size: 14px;
          line-height: 1.6;
        }
        .wysiwyg-editor .ql-editor img {
          max-width: 100%;
          height: auto;
          border-radius: 4px;
          margin: 8px 0;
        }
        .wysiwyg-editor .ql-toolbar {
          border: 1px solid #e5e7eb;
          border-bottom: none;
          border-radius: 8px 8px 0 0;
        }
        .wysiwyg-editor .ql-container {
          border: 1px solid #e5e7eb;
          border-top: none;
          border-radius: 0 0 8px 8px;
          height: auto;
        }
        .wysiwyg-editor .ql-editor.ql-blank::before {
          color: #9ca3af;
          font-style: italic;
        }
      `}</style>
    </div>
  );
};

export default WysiwygEditor;
